import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { hideOtpModal, unlockActions } from "../../store/actions";
import { notifySuccess } from "../Custom/notification";
import api from "../../api/api";
import { Skeleton } from "@mui/material";

// Custom styled Slot component for input-otp
const Slot = (props) => {
  return (
    <div
      style={{
        width: "40px",
        height: "50px",
        border: props.isActive ? "2px solid #556ee6" : "1px solid #ced4da",
        borderRadius: "0.25rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "24px",
        backgroundColor: props.isActive ? "#f8f9fa" : "white",
        margin: "0 4px",
        boxShadow: props.isActive
          ? "0 0 0 0.15rem rgba(85, 110, 230, 0.25)"
          : "none",
        outline: "none",
        color: "#495057",
        transition:
          "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
      }}
    >
      {props.char !== null ? props.char : props.hasFakeCaret ? "|" : ""}
    </div>
  );
};

const OtpUnlockModal = () => {
  const [otp, setOtp] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.otpUnlock.isModalOpen);
  const user = useSelector((state) => state.Login);

  const requiresOtp =
    user?.user?.requires_otp_for_update ??
    user?.requires_otp_for_update ??
    true;

  const toggle = () => {
    dispatch(hideOtpModal());
    setOtp("");
    setErrorMsg("");
  };

  useEffect(() => {
    if (requiresOtp === false) {
      dispatch(unlockActions(null));
      if (isOpen) {
        dispatch(hideOtpModal());
      }
    }
  }, [requiresOtp, isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && requiresOtp !== false) {
      const userEmail = user?.user?.email || user?.email;

      if (userEmail) {
        setIsLoadingOtp(true);
        api
          .postWithToken("/admin/request_old_doc_access", { email: userEmail })
          .then((res) => {
            setIsLoadingOtp(false);
            const responseData = res.data?.data || res.data;
            if (responseData && !responseData.error) {
              notifySuccess(
                responseData.message || "OTP sent successfully to your email.",
              );
            } else {
              setErrorMsg(
                responseData?.error?.message ||
                  responseData?.message ||
                  "Failed to send OTP.",
              );
            }
          })
          .catch((err) => {
            setIsLoadingOtp(false);
            console.error("Error sending OTP", err);
            const beError =
              err.response?.data?.error?.message || err.response?.data?.message;
            setErrorMsg(beError || "Failed to send OTP to your email.");
          });
      } else {
        setErrorMsg("User email not found. Cannot send OTP.");
      }
    }
  }, [isOpen, requiresOtp, user]);

  const handleVerify = () => {
    if (!otp || otp.length !== 6) {
      setErrorMsg("Please enter a complete 6-digit OTP.");
      return;
    }

    api
      .postWithToken("/admin/verify_old_doc_access", { otp })
      .then((res) => {
        const responseData = res.data?.data || res.data;
        if (responseData && !responseData.error) {
          let expiresAt = null;
          if (responseData.data && responseData.data.expiresAt) {
            expiresAt = new Date(responseData.data.expiresAt).getTime();
          } else {
            expiresAt = Date.now() + 5 * 60 * 1000;
          }
          dispatch(unlockActions(expiresAt));
          notifySuccess(responseData.message || "Access granted.");
          setOtp("");
          setErrorMsg("");
          dispatch(hideOtpModal());
        } else {
          setErrorMsg(
            responseData?.error?.message ||
              responseData?.message ||
              "Invalid OTP.",
          );
        }
      })
      .catch((err) => {
        console.error("Error verifying OTP", err);
        const beError =
          err.response?.data?.error?.message || err.response?.data?.message;
        setErrorMsg(beError || "Invalid OTP or verification failed.");
      });
  };

  const handleOtpChange = (value) => {
    setOtp(value);
    if (errorMsg) setErrorMsg("");
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Enter OTP to Unlock Actions</ModalHeader>
      <ModalBody>
        {isLoadingOtp ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-2">
            <Skeleton variant="text" width="80%" height={24} className="mb-4" />
            <div className="d-flex justify-content-center mb-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width={40}
                  height={50}
                  sx={{ mx: 0.5, borderRadius: "0.25rem" }}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <p className="text-center text-muted mb-4">
              Please enter the 6-digit OTP to perform edit or delete actions.
            </p>
            <div className="d-flex justify-content-center mb-3">
              <OTPInput
                maxLength={6}
                value={otp}
                onChange={handleOtpChange}
                pattern={REGEXP_ONLY_DIGITS}
                render={({ slots }) => (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {slots.map((slot, idx) => (
                      <Slot key={idx} {...slot} />
                    ))}
                  </div>
                )}
              />
            </div>
            {errorMsg && (
              <div className="text-danger text-center mt-2 fw-medium">
                {errorMsg}
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isLoadingOtp}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleVerify} disabled={isLoadingOtp}>
          Verify OTP
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OtpUnlockModal;
